import { useEffect, useMemo } from 'react';
import { Loader } from 'mobily-ui-library';
import { useShallow } from 'zustand/shallow';
import { useTranslation } from 'react-i18next';
import { useGlobalStore, type CustomWindow } from './stores/globalStore';
import { APIError } from './components/APIError';
import { useCheckoutStore } from './stores/checkoutStore';
import { StepsMenu } from './components/StepsMenu';
import { LocationForm } from './components/LocationForm';
import { useAppInitializer } from './hooks/useAppInitializer';
import { MeshExtender } from './components/MeshExtender';
import { RequestDetails } from './components/RequestDetails';
import { OTPScreen } from './components/OTPScreen';
import { OrderSummary } from './components/OrderSummary';
import { SuccessScreen } from './components/SuccessScreen';

import './App.scss';
import i18n from './i18n';

const SECTION_MAP: Record<string, React.FC> = {
  "OTPVerification": OTPScreen,
  "OrderSummary": OrderSummary,
  "SuccessPage": SuccessScreen,
};

const STEP_MAP: Record<string, React.FC> = {
  "Location-Details": LocationForm,
  "Addon-Details": MeshExtender,
  "FTTH-Customer-Details": RequestDetails,
};

const App = () => {
  const { t } = useTranslation();
  useAppInitializer();
  const {
    loading,
    apiError,
  } = useGlobalStore(
    useShallow((state) => ({
      loading: state.loading,
      apiError: state.apiError,
    }))
  );

  const { currentSection, currentStep } = useCheckoutStore(
    useShallow((state) => ({
      currentSection: state.currentSection,
      currentStep: state.currentStep,
    }))
  );

  const currentComponent = useMemo(() => {
    if (!currentSection) return null;

    if (currentStep) {
      const StepComponent = STEP_MAP[currentStep.uiRef];
      if (!StepComponent) {
        console.warn(`No component found for uiRef: ${currentStep.uiRef}`);
        return null;
      }
      return (
        <>
          <StepsMenu />
          <StepComponent />
        </>
      );
    }

    const SectionComponent = SECTION_MAP[currentSection.sectionId];
    if (!SectionComponent) {
      console.warn(`No component found for sectionId: ${currentSection.sectionId}`);
      return null;
    }
    if (currentSection.sectionId === "SuccessPage") {
      return <SectionComponent />;
    }
    return (
      <>
        <StepsMenu />
        <SectionComponent />
      </>
    );
  }, [currentSection, currentStep]);

  // effect

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const win = window as CustomWindow;

    useGlobalStore.setState({
      lang: win.lang ?? 'en',
      rtl: win.lang === 'ar',
      apiKey: win.x_api_key ?? win.apigee_bm_x_api_key,
      token: win.clientToken,
      productKey: sessionStorage.getItem('productKey') || '',
    });
    i18n.changeLanguage(win.lang ?? 'en');
  }, []);

  const showApiError = apiError && apiError.isError;

  return (
    <div className="appContainer" >
      <Loader loading={loading} message={t('please_Wait')} />
      {currentComponent}
      {showApiError && <APIError />}
    </div>
  );
};

export default App;
